import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import { useCategories } from '../hooks/useCategories';
import './Recurring.css';

function Recurring() {
  const { wallets, selectedWallets } = useWallets();
  const { categories, loading: categoriesLoading } = useCategories();
  
  const [recurring, setRecurring] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [skipSplitCalc, setSkipSplitCalc] = useState(false); // Skip split calculation when loading edit data
  
  // Household split state
  const [isHouseholdWallet, setIsHouseholdWallet] = useState(false);
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [splits, setSplits] = useState([]);
  
  const [formData, setFormData] = useState({
    wallet_id: '',
    type: 'expense',
    budget_group: '', // New field for budget group filter
    category: '',
    subcategory: '',
    amount: '',
    description: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    // Split fields for household wallets
    split_type: 'equal',
    payment_mode: 'shared',
    paid_by_user_id: null
  });

  useEffect(() => {
    fetchData();
  }, [selectedWallets]); // Re-fetch when selected wallets change

  // Detect household wallet and load members
  // Skip if we're editing (editingId is set) - handleEdit manages this
  useEffect(() => {
    // Skip if editing - handleEdit handles member loading
    if (editingId) {
      return;
    }
    
    if (formData.wallet_id) {
      const wallet = wallets.find(w => w.id === parseInt(formData.wallet_id));
      if (wallet && wallet.wallet_type === 'household' && wallet.household_id) {
        setIsHouseholdWallet(true);
        loadHouseholdMembers(wallet.household_id);
      } else {
        setIsHouseholdWallet(false);
        setHouseholdMembers([]);
        setSplits([]);
      }
    } else {
      setIsHouseholdWallet(false);
      setHouseholdMembers([]);
      setSplits([]);
    }
  }, [formData.wallet_id, wallets, editingId]);

  // Recalculate splits when amount or split_type changes
  // Skip when loading edit data (skipSplitCalc is true)
  useEffect(() => {
    if (skipSplitCalc) {
      console.log('  Skipping split calculation (loading edit data)');
      return;
    }
    if (formData.amount && householdMembers.length > 0 && isHouseholdWallet) {
      calculateSplits();
    }
  }, [formData.amount, formData.split_type, householdMembers, isHouseholdWallet, skipSplitCalc]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recurringRes, upcomingRes] = await Promise.all([
        api.get('/recurring'),
        api.get('/recurring/upcoming?days=60')
      ]);
      console.log('📊 Recurring data:', recurringRes.data);
      console.log('📅 Upcoming data:', upcomingRes.data);
      console.log('💼 Selected wallets:', selectedWallets);
      setRecurring(recurringRes.data);
      setUpcoming(upcomingRes.data);
    } catch (error) {
      console.error('Error fetching recurring transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHouseholdMembers = async (householdId) => {
    try {
      const response = await api.get(`/household/${householdId}/members`);
      setHouseholdMembers(response.data);
      calculateInitialSplits(response.data);
    } catch (error) {
      console.error('Error loading household members:', error);
    }
  };

  const calculateInitialSplits = (membersList) => {
    const initialSplits = membersList.map(member => ({
      user_id: member.user_id,
      user_name: member.name,
      split_amount: 0,
      split_percentage: 100 / membersList.length
    }));
    setSplits(initialSplits);
  };

  const calculateSplits = () => {
    const amount = parseFloat(formData.amount) || 0;
    
    if (formData.split_type === 'equal') {
      const equalAmount = amount / householdMembers.length;
      setSplits(householdMembers.map(member => ({
        user_id: member.user_id,
        user_name: member.name,
        split_amount: equalAmount,
        split_percentage: 100 / householdMembers.length
      })));
    } else if (formData.split_type === 'percentage') {
      setSplits(prev => prev.map(split => ({
        ...split,
        split_amount: amount * (split.split_percentage / 100)
      })));
    }
  };

  const handleSplitTypeChange = (type) => {
    setFormData({ ...formData, split_type: type });
    if (type === 'custom') {
      const amount = parseFloat(formData.amount) || 0;
      const equalAmount = amount / householdMembers.length;
      setSplits(householdMembers.map(member => ({
        user_id: member.user_id,
        user_name: member.name,
        split_amount: equalAmount,
        split_percentage: 100 / householdMembers.length
      })));
    }
  };

  const handlePercentageChange = (userId, percentage) => {
    const newPercentage = parseFloat(percentage) || 0;
    setSplits(prev => prev.map(split => 
      split.user_id === userId 
        ? { 
            ...split, 
            split_percentage: newPercentage,
            split_amount: (parseFloat(formData.amount) || 0) * (newPercentage / 100)
          }
        : split
    ));
  };

  const handleCustomAmountChange = (userId, amount) => {
    const newAmount = parseFloat(amount) || 0;
    setSplits(prev => prev.map(split =>
      split.user_id === userId
        ? { ...split, split_amount: newAmount }
        : split
    ));
  };

  const validateSplits = () => {
    if (!isHouseholdWallet || formData.type !== 'expense') {
      return true;
    }

    const totalAmount = parseFloat(formData.amount) || 0;
    
    if (formData.split_type === 'percentage') {
      const totalPercentage = splits.reduce((sum, split) => sum + (split.split_percentage || 0), 0);
      if (Math.abs(totalPercentage - 100) > 0.01) {
        alert(`Percentages must add up to 100% (currently ${totalPercentage.toFixed(1)}%)`);
        return false;
      }
    } else if (formData.split_type === 'custom') {
      const totalSplit = splits.reduce((sum, split) => sum + (split.split_amount || 0), 0);
      if (Math.abs(totalSplit - totalAmount) > 0.01) {
        alert(`Split amounts must equal total (${totalSplit.toFixed(2)} ≠ ${totalAmount.toFixed(2)})`);
        return false;
      }
    }
    
    return true;
  };

  const getTotalSplit = () => {
    return splits.reduce((sum, split) => sum + (split.split_amount || 0), 0);
  };

  const getTotalPercentage = () => {
    return splits.reduce((sum, split) => sum + (split.split_percentage || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSplits()) {
      return;
    }
    
    try {
      const payload = { ...formData };
      
      // Add splits for household wallets
      if (isHouseholdWallet && formData.type === 'expense') {
        const wallet = wallets.find(w => w.id === parseInt(formData.wallet_id));
        payload.household_id = wallet.household_id;
        payload.splits = splits.map(split => ({
          user_id: split.user_id,
          split_amount: split.split_amount,
          split_percentage: formData.split_type === 'percentage' ? split.split_percentage : null
        }));
        
        // Ensure paid_by_user_id is set for paid_by_creator mode
        if (formData.payment_mode === 'paid_by_creator' && !formData.paid_by_user_id) {
          // Get current user ID (the creator)
          try {
            const profileRes = await api.get('/auth/profile');
            payload.paid_by_user_id = profileRes.data.id;
          } catch (e) {
            console.error('Could not get current user ID:', e);
          }
        }
      }
      
      console.log('📤 Submitting payload:', payload);
      console.log('  payment_mode:', payload.payment_mode);
      console.log('  paid_by_user_id:', payload.paid_by_user_id);
      console.log('  household_id:', payload.household_id);
      console.log('  splits:', payload.splits);
      
      if (editingId) {
        await api.put(`/recurring/${editingId}`, payload);
      } else {
        await api.post('/recurring', payload);
      }
      
      setShowModal(false);
      resetForm();
      
      // Automatically process recurring transactions after creating/editing
      try {
        await api.post('/recurring/process');
        console.log('✅ Recurring transactions processed automatically');
      } catch (processError) {
        console.error('Error auto-processing:', processError);
      }
      
      fetchData();
    } catch (error) {
      console.error('Error saving recurring transaction:', error);
      alert('Failed to save recurring transaction');
    }
  };

  const handleEdit = async (rec) => {
    console.log('📝 Editing recurring transaction:', rec);
    console.log('  payment_mode from API:', rec.payment_mode);
    console.log('  paid_by_user_id from API:', rec.paid_by_user_id);
    console.log('  split_config from API:', rec.split_config);
    
    // Get budget group for the category
    const budgetGroup = categories[rec.category]?.budget_group || '';
    
    // Check if this is a household wallet
    const selectedWallet = wallets.find(w => w.id === rec.wallet_id);
    const isHousehold = selectedWallet && selectedWallet.wallet_type === 'household';
    
    // If household wallet, load members FIRST before setting form data
    // This prevents the useEffect from overwriting our split_config
    let loadedMembers = [];
    if (isHousehold && selectedWallet.household_id) {
      try {
        const response = await api.get(`/household/${selectedWallet.household_id}/members`);
        loadedMembers = response.data;
        setHouseholdMembers(loadedMembers);
        setIsHouseholdWallet(true);
      } catch (error) {
        console.error('Error loading household members:', error);
      }
    }
    
    // Now set the form data
    setEditingId(rec.id);
    setFormData({
      wallet_id: rec.wallet_id,
      type: rec.type,
      budget_group: budgetGroup,
      category: rec.category,
      subcategory: rec.subcategory || '',
      amount: rec.amount,
      description: rec.description || '',
      frequency: rec.frequency,
      start_date: rec.start_date,
      end_date: rec.end_date || '',
      payment_mode: rec.payment_mode || 'paid_by_creator',
      paid_by_user_id: rec.paid_by_user_id || null,
      split_type: 'equal' // Default, will be overridden by split_config if exists
    });
    
    // Restore splits if they exist - add user names from loaded members
    if (rec.split_config) {
      try {
        const splitConfig = typeof rec.split_config === 'string' 
          ? JSON.parse(rec.split_config) 
          : rec.split_config;
        console.log('  Parsed split_config:', splitConfig);
        
        // Enrich splits with user names from loaded members
        const enrichedSplits = splitConfig.map(split => {
          const member = loadedMembers.find(m => m.user_id === split.user_id);
          return {
            ...split,
            user_name: member ? member.name : `User ${split.user_id}`
          };
        });
        
        console.log('  Enriched splits:', enrichedSplits);
        setSplits(enrichedSplits);
      } catch (error) {
        console.error('Error parsing split config:', error);
        // Initialize with default splits if parsing fails
        if (isHousehold && loadedMembers.length > 0) {
          const amount = parseFloat(rec.amount) || 0;
          const equalAmount = amount / loadedMembers.length;
          setSplits(loadedMembers.map(member => ({
            user_id: member.user_id,
            user_name: member.name,
            split_amount: equalAmount,
            split_percentage: 100 / loadedMembers.length
          })));
        }
      }
    } else if (isHousehold && loadedMembers.length > 0) {
      // Initialize with default splits if none exist
      const amount = parseFloat(rec.amount) || 0;
      const equalAmount = amount / loadedMembers.length;
      setSplits(loadedMembers.map(member => ({
        user_id: member.user_id,
        user_name: member.name,
        split_amount: equalAmount,
        split_percentage: 100 / loadedMembers.length
      })));
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recurring transaction?')) {
      return;
    }
    
    try {
      await api.delete(`/recurring/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting recurring transaction:', error);
      alert('Failed to delete recurring transaction');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/recurring/${id}`, { is_active: !currentStatus });
      fetchData();
    } catch (error) {
      console.error('Error toggling recurring transaction:', error);
    }
  };

  const handleBudgetGroupChange = (e) => {
    const budgetGroup = e.target.value;
    setFormData({
      ...formData,
      budget_group: budgetGroup,
      category: '', // Reset category when budget group changes
      subcategory: ''
    });
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    const subcategories = categories[category]?.subcategories || [];
    setFormData({
      ...formData,
      category,
      subcategory: subcategories[0] || ''
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setIsHouseholdWallet(false);
    setHouseholdMembers([]);
    setSplits([]);
    setFormData({
      wallet_id: '',
      type: 'expense',
      budget_group: '', // Reset budget group too
      category: '',
      subcategory: '',
      amount: '',
      description: '',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      split_type: 'equal',
      payment_mode: 'shared',
      paid_by_user_id: null
    });
  };

  const getWalletName = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet ? wallet.name : 'Unknown';
  };

  const getWalletCurrency = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet ? wallet.currency : 'EUR';
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CNY': '¥', 'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  const getFrequencyIcon = (frequency) => {
    switch (frequency) {
      case 'daily': return '📅';
      case 'weekly': return '📆';
      case 'monthly': return '🗓️';
      case 'yearly': return '📊';
      default: return '🔄';
    }
  };

  // Filter categories by budget group
  const getFilteredCategories = () => {
    if (!formData.budget_group) {
      return Object.keys(categories).sort();
    }
    
    return Object.keys(categories)
      .filter(cat => categories[cat].budget_group === formData.budget_group)
      .sort();
  };

  const filteredCategoryKeys = getFilteredCategories();
  const currentSubcategories = formData.category ? (categories[formData.category]?.subcategories || []) : [];

  // Filter recurring transactions by selected wallets
  const filteredRecurring = recurring.filter(rec => selectedWallets.includes(rec.wallet_id));
  const filteredUpcoming = upcoming.filter(item => selectedWallets.includes(item.wallet_id));

  if (loading) {
    return (
      <div className="recurring-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading recurring transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recurring-container">
      <div className="recurring-header">
        <h1>🔄 Recurring Transactions</h1>
        <button className="add-btn" onClick={() => { resetForm(); setShowModal(true); }}>
          + Add Recurring
        </button>
      </div>

      <div className="recurring-content">
        {/* Active Recurring Transactions */}
        <div className="recurring-section">
          <h2>Active Recurring Transactions</h2>
          {filteredRecurring.filter(r => r.is_active).length === 0 ? (
            <div className="empty-state">
              <p>No active recurring transactions for selected pot(s)</p>
              <button className="add-btn-small" onClick={() => { resetForm(); setShowModal(true); }}>
                + Add Your First Recurring Transaction
              </button>
            </div>
          ) : (
            <div className="recurring-list">
              {filteredRecurring.filter(r => r.is_active).map(rec => {
                const currency = getWalletCurrency(rec.wallet_id);
                const currencySymbol = getCurrencySymbol(currency);
                return (
                  <div key={rec.id} className={`recurring-card ${rec.type}`}>
                    <div className="recurring-main">
                      <div className="recurring-info">
                        <div className="recurring-title">
                          <span className="frequency-icon">{getFrequencyIcon(rec.frequency)}</span>
                          <span className="category">{rec.category}</span>
                          {rec.subcategory && <span className="subcategory">• {rec.subcategory}</span>}
                        </div>
                        <div className="recurring-details">
                          <span className="amount">{currencySymbol}{parseFloat(rec.amount).toFixed(2)}</span>
                          <span className="frequency">{rec.frequency}</span>
                          <span className="wallet">{getWalletName(rec.wallet_id)} ({currency})</span>
                        </div>
                        {rec.description && <p className="description">{rec.description}</p>}
                        <div className="recurring-dates">
                          <span>From: {rec.start_date}</span>
                          {rec.end_date && <span>Until: {rec.end_date}</span>}
                          {!rec.end_date && <span className="ongoing">Ongoing</span>}
                        </div>
                      </div>
                      <div className="recurring-actions">
                        <button className="action-btn edit" onClick={() => handleEdit(rec)} title="Edit">
                          ✏️
                        </button>
                        <button 
                          className="action-btn toggle" 
                          onClick={() => handleToggleActive(rec.id, rec.is_active)}
                          title="Pause"
                        >
                          ⏸️
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(rec.id)} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Inactive Recurring Transactions */}
        {filteredRecurring.filter(r => !r.is_active).length > 0 && (
          <div className="recurring-section">
            <h2>Paused Recurring Transactions</h2>
            <div className="recurring-list">
              {filteredRecurring.filter(r => !r.is_active).map(rec => {
                const currency = getWalletCurrency(rec.wallet_id);
                const currencySymbol = getCurrencySymbol(currency);
                return (
                  <div key={rec.id} className={`recurring-card ${rec.type} inactive`}>
                    <div className="recurring-main">
                      <div className="recurring-info">
                        <div className="recurring-title">
                          <span className="frequency-icon">{getFrequencyIcon(rec.frequency)}</span>
                          <span className="category">{rec.category}</span>
                        </div>
                        <div className="recurring-details">
                          <span className="amount">{currencySymbol}{parseFloat(rec.amount).toFixed(2)}</span>
                          <span className="frequency">{rec.frequency}</span>
                        </div>
                      </div>
                      <div className="recurring-actions">
                        <button 
                          className="action-btn toggle" 
                          onClick={() => handleToggleActive(rec.id, rec.is_active)}
                          title="Resume"
                        >
                          ▶️
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(rec.id)} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Transactions Preview */}
        <div className="upcoming-section">
          <h2>📅 Upcoming (Next 60 Days)</h2>
          {filteredUpcoming.length === 0 ? (
            <p className="empty-state">No upcoming transactions for selected pot(s)</p>
          ) : (
            <div className="upcoming-list">
              {filteredUpcoming.slice(0, 10).map((item, index) => {
                const currency = getWalletCurrency(item.wallet_id);
                const currencySymbol = getCurrencySymbol(currency);
                return (
                  <div key={index} className={`upcoming-item ${item.type}`}>
                    <span className="date">{new Date(item.date).toLocaleDateString()}</span>
                    <span className="category">{item.category}</span>
                    <span className={`amount ${item.type}`}>
                      {item.type === 'income' ? '+' : '-'}{currencySymbol}{parseFloat(item.amount).toFixed(2)}
                    </span>
                  </div>
                );
              })}
              {filteredUpcoming.length > 10 && (
                <p className="more-indicator">+ {filteredUpcoming.length - 10} more upcoming...</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Recurring Transaction' : 'Add Recurring Transaction'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value, category: '', subcategory: '' })}
                  required
                >
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>

              <div className="form-group">
                <label>Pot</label>
                <select
                  value={formData.wallet_id}
                  onChange={(e) => setFormData({ ...formData, wallet_id: parseInt(e.target.value) })}
                  required
                >
                  <option value="">Select Pot</option>
                  {wallets.filter(w => w.id !== 0).map(wallet => (
                    <option key={wallet.id} value={wallet.id}>{wallet.name}</option>
                  ))}
                </select>
              </div>

              {/* INCOME: Source field (text input) */}
              {formData.type === 'income' && (
                <div className="form-group">
                  <label>Source *</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Salary, Freelance, Bonus"
                    required
                  />
                </div>
              )}

              {/* EXPENSE: Budget Group, Category & Subcategory dropdowns */}
              {formData.type === 'expense' && (
                <>
                  <div className="form-group">
                    <label>Budget Group *</label>
                    <select 
                      value={formData.budget_group} 
                      onChange={handleBudgetGroupChange} 
                      required
                      disabled={categoriesLoading}
                    >
                      <option value="">Select budget group...</option>
                      <option value="needs">🏠 Needs</option>
                      <option value="wants">🎉 Wants</option>
                      <option value="savings">💰 Savings</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Category *</label>
                      <select 
                        value={formData.category} 
                        onChange={handleCategoryChange} 
                        required 
                        disabled={categoriesLoading || !formData.budget_group}
                      >
                        <option value="">
                          {!formData.budget_group ? 'Select budget group first...' : 'Select category...'}
                        </option>
                        {filteredCategoryKeys.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Subcategory *</label>
                      <select 
                        value={formData.subcategory} 
                        onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                        disabled={!formData.category}
                        required
                      >
                        <option value="">Select subcategory...</option>
                        {currentSubcategories.map(sub => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Amount {formData.wallet_id ? `(${getCurrencySymbol(getWalletCurrency(formData.wallet_id))})` : ''}</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional details"
                />
              </div>

              {/* Show splits ONLY for household wallets + expenses */}
              {isHouseholdWallet && formData.type === 'expense' && (
                <>
                  {/* Payment Mode Section */}
                  <div className="form-section">
                    <h3>💳 Payment Mode</h3>
                    <div className="payment-mode-selector">
                      <label className={`mode-option ${formData.payment_mode === 'shared' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="payment_mode"
                          value="shared"
                          checked={formData.payment_mode === 'shared'}
                          onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                        />
                        <div>
                          <span className="mode-icon">🏦</span>
                          <span className="mode-label">Paid from Shared Account</span>
                          <span className="mode-desc">No debt created</span>
                        </div>
                      </label>

                      <label className={`mode-option ${formData.payment_mode === 'paid_by_creator' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="payment_mode"
                          value="paid_by_creator"
                          checked={formData.payment_mode === 'paid_by_creator'}
                          onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                        />
                        <div>
                          <span className="mode-icon">👤</span>
                          <span className="mode-label">I Paid for Everyone</span>
                          <span className="mode-desc">Others owe me their share</span>
                        </div>
                      </label>

                      <label className={`mode-option ${formData.payment_mode === 'paid_by_other' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="payment_mode"
                          value="paid_by_other"
                          checked={formData.payment_mode === 'paid_by_other'}
                          onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                        />
                        <div>
                          <span className="mode-icon">👥</span>
                          <span className="mode-label">Someone Else Paid</span>
                          <span className="mode-desc">I owe them my share</span>
                        </div>
                      </label>
                    </div>

                    {formData.payment_mode === 'paid_by_other' && (
                      <div className="form-group" style={{marginTop: '16px'}}>
                        <label>Who Paid? *</label>
                        <select
                          value={formData.paid_by_user_id || ''}
                          onChange={(e) => setFormData({ ...formData, paid_by_user_id: parseInt(e.target.value) })}
                          required
                        >
                          <option value="">Select member...</option>
                          {householdMembers.map(member => (
                            <option key={member.user_id} value={member.user_id}>
                              {member.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Split Type Section */}
                  <div className="form-section">
                    <h3>✂️ Split Type</h3>
                    <div className="split-type-selector">
                      <button
                        type="button"
                        className={`split-type-btn ${formData.split_type === 'equal' ? 'selected' : ''}`}
                        onClick={() => handleSplitTypeChange('equal')}
                      >
                        <span className="split-icon">⚖️</span>
                        <span>Equal Split</span>
                      </button>
                      <button
                        type="button"
                        className={`split-type-btn ${formData.split_type === 'percentage' ? 'selected' : ''}`}
                        onClick={() => handleSplitTypeChange('percentage')}
                      >
                        <span className="split-icon">%</span>
                        <span>Percentage</span>
                      </button>
                      <button
                        type="button"
                        className={`split-type-btn ${formData.split_type === 'custom' ? 'selected' : ''}`}
                        onClick={() => handleSplitTypeChange('custom')}
                      >
                        <span className="split-icon">✏️</span>
                        <span>Custom Amounts</span>
                      </button>
                    </div>

                    {/* Split Preview */}
                    <div className="split-preview">
                      <h4>Split Breakdown</h4>
                      <div className="split-list">
                        {splits.map((split) => (
                          <div key={split.user_id} className="split-item">
                            <div className="split-member">{split.user_name}</div>
                            <div className="split-inputs">
                              {formData.split_type === 'percentage' && (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={split.split_percentage}
                                  onChange={(e) => handlePercentageChange(split.user_id, e.target.value)}
                                  className="percentage-input"
                                  placeholder="0"
                                />
                              )}
                              {formData.split_type === 'percentage' && <span className="percent-sign">%</span>}
                              
                              {formData.split_type === 'custom' ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={split.split_amount}
                                  onChange={(e) => handleCustomAmountChange(split.user_id, e.target.value)}
                                  className="amount-input"
                                  placeholder="0.00"
                                />
                              ) : (
                                <div className="amount-display">
                                  {split.split_amount.toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Validation Display */}
                      <div className="split-totals">
                        {formData.split_type === 'percentage' && (
                          <div className={`total-row ${Math.abs(getTotalPercentage() - 100) < 0.01 ? 'valid' : 'invalid'}`}>
                            <span>Total Percentage:</span>
                            <span className="total-value">{getTotalPercentage().toFixed(1)}%</span>
                          </div>
                        )}
                        <div className={`total-row ${Math.abs(getTotalSplit() - parseFloat(formData.amount || 0)) < 0.01 ? 'valid' : 'invalid'}`}>
                          <span>Total Split:</span>
                          <span className="total-value">{getTotalSplit().toFixed(2)}</span>
                        </div>
                        <div className="total-row">
                          <span>Expense Amount:</span>
                          <span className="total-value">{parseFloat(formData.amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date <span className="optional-label">(Leave empty for ongoing)</span></label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    placeholder="Optional - Leave empty for ongoing"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Recurring;
